import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDrop } from 'react-dnd';

import { InteractiveClickOrDrag } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag';
import InteractiveDragDrop from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop';
import {
  CANCELLED,
  DragItem,
  INTERACTIVE_CANVAS_ID,
  INTERACTIVE_CONTAINER_ID,
  INTERACTIVE_DRAG_DROP,
  PLACED,
} from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.constants';
import { isEveryDraggableDropped } from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-drop.utils';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { useSortableScrollManager } from 'approot/shared/sortable/sortable.hooks';
import { InteractiveResponseCheckAnswer } from '../../shared/interactive-response-check-answer';
import {
  InteractiveResponseHelpControls,
  InteractiveResponseStartAgainControls,
} from '../../shared/interactive-response-controls';
import { InteractiveResponseControlsBottom } from '../../shared/interactive-response-controls.style';
import {
  InteractiveResponseScreenReader,
  InteractiveResponseTitle,
} from '../../shared/interactive-response-shared';
import {
  useInteractiveAudio,
  useInteractiveClickOrDrag,
} from '../../shared/interactive-response-shared.hooks';
import {
  InteractiveCanvas,
  InteractiveCanvasDragZone,
  InteractiveCanvasDragZoneOuter,
  InteractiveResponsePlayContainer,
  InteractiveResponseWrapper,
  InteractiveStackDragContainer,
} from '../../shared/interactive-response-shared.style';
import {
  isEverythingCorrect as checkIsEverythingCorrect,
  isDropZoneFull,
  returnItemToOriginalPosition,
} from '../../shared/interactive-response-shared.utils';
import {
  CATEGORISE_DROP_ITEM_SIZE,
  INITIAL_SETUP_DURATION,
  InteractiveCategoriseAttempt,
  RESPONSE_FREE,
  ViewProps,
} from '../interactive-response-categorise.constants';
import { getShouldShowNumberOfItemsHint } from '../settings/interactive-response-categorise.utils';
import { InteractiveDrop } from './interactive-response-categorise-drop';
import { InteractiveDropDisplay } from './interactive-response-categorise-drop-display';
import {
  initialise,
  populateAnswers,
  resetIncorrectItems,
} from './interactive-response-categorise-play.utils';
import { handleItemReturn } from '../../shared/interactive-response-shared.utils';
import { useCategorise } from './interactive-response-categorise.hooks';
import * as Styled from './interactive-response-categorise.style';
import { useInteractiveAudioContext } from '../../shared/interactive-audio.context';
import { useActivityBuilderPresentContext } from 'approot/activity-builder/present/activity-builder-present.context';
import InteractiveCategoriseFree from './interactive-categorise-free';
import {
  InteractiveResponseSelectAreaSnackbar,
  InteractiveResponseSelectItemSnackbar,
} from '../../shared/interactive-response-snackbars';

type Props = ViewProps & {
  attemptAnswers?: InteractiveResponseAnswerFragment;
  onExpandImage: (id: string, dragItems: DragItem[]) => void;
  onAnswerAttempt: InteractiveCategoriseAttempt;
  showAnswer: boolean;
};

const Interactive = ({
  properties,
  attemptAnswers,
  onAnswerAttempt,
  onExpandImage,
}: Props) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const [isTransitioning, setIsTransitioning] = useState<boolean>(true);
  const [playAttempt, setPlayAttempt] = useState<number>(1);

  const categoriseProperties = properties.interactiveResponseCategorise;

  const isCheckAnswerAtEnd = categoriseProperties.checkAnswerAtEnd;

  const { contentType } = useActivityBuilderPresentContext();
  const isShowNumberOfItemsHint = getShouldShowNumberOfItemsHint(
    categoriseProperties.showNumberOfItemsHint,
    contentType
  );

  const { setAudioList } = useInteractiveAudioContext();

  const { toggleAudio, playClick, playStart, playAudio } =
    useInteractiveAudio();

  const {
    dragItems,
    dropItems,
    keyboardState,
    setDragItems,
    setDropItems,
    handleOnDrop,
    handleCheckAnswers,
    handleKeyboardInteraction,
  } = useCategorise({
    playClick,
    isCheckAnswerAtEnd,
    showAnswer: true,
    onAnswerAttempt,
  });

  const {
    dropZoneSelected,
    dragItemSelected,
    handleItemReturnToStack,
    handleDroppedDragItemClick,
    handleDragItemClick,
    handleDropZoneClick,
  } = useInteractiveClickOrDrag();

  const keyboardProps = {
    target:
      keyboardState?.action === CANCELLED || keyboardState?.action === PLACED
        ? undefined
        : keyboardState?.id,
    onKeyboardInteraction: handleKeyboardInteraction,
  };

  const initialiseItems = useCallback(
    (startAgain?: boolean) => {
      const { dragItems, dropItems } =
        attemptAnswers && !startAgain
          ? populateAnswers({
              attemptedAnswers: attemptAnswers,
              items: categoriseProperties.items,
              categories: categoriseProperties.categories,
              variant: categoriseProperties.variant,
              isShowNumberOfItemsHint,
              showAnswer: true,
            })
          : initialise({
              items: categoriseProperties.items,
              categories: categoriseProperties.categories,
              showAnswer: true,
              isShowNumberOfItemsHint,
              shouldShuffle: true,
              variant: categoriseProperties.variant,
            });
      setIsTransitioning(true);
      setDropItems(dropItems);
      setDragItems(dragItems);
      setPlayAttempt(playAttempt + 1);
    },
    [
      playAttempt,
      isShowNumberOfItemsHint,
      categoriseProperties.items,
      categoriseProperties.categories,
      categoriseProperties.variant,
      setDragItems,
      setDropItems,
      setPlayAttempt,
      setIsTransitioning,
      attemptAnswers,
    ]
  );

  const setup = useCallback(
    (startAgain?: boolean) => {
      initialiseItems(startAgain);
      setTimeout(() => {
        playStart();
      }, 400);
      setTimeout(() => {
        setIsTransitioning(false);
      }, INITIAL_SETUP_DURATION);
    },
    [initialiseItems, playStart]
  );

  const tryAgain = useCallback(() => {
    const { dragItems: updatedDragItems, dropItems: updatedDropItems } =
      resetIncorrectItems(dragItems, dropItems);

    setDragItems(updatedDragItems);
    setDropItems(updatedDropItems);
    setIsTransitioning(true);

    setTimeout(() => {
      playStart();
    }, 400);
    setTimeout(() => {
      setIsTransitioning(false);
    }, INITIAL_SETUP_DURATION);
  }, [dragItems, dropItems, setDragItems, setDropItems, playStart]);

  const cancelKeyboardInteraction = useCallback(() => {
    if (
      keyboardState &&
      keyboardState.action !== CANCELLED &&
      keyboardState.action !== PLACED
    ) {
      handleKeyboardInteraction({
        action: CANCELLED,
        id: keyboardState.id,
        announce: 'You have cancelled keyboard interaction',
      });
    }
  }, [keyboardState, handleKeyboardInteraction]);

  const isEverythingAnswered = useMemo(() => {
    return isEveryDraggableDropped(dragItems);
  }, [dragItems]);

  const hasCheckedAnswer = useMemo(() => {
    /**
     * Determines if any zone in any drop item has been checked for correctness.
     * Returns true if at least one zone has isCorrect set (true or false).
     */
    return (
      dropItems?.some(dropItem =>
        dropItem?.zones?.some(zone => zone.isCorrect !== undefined)
      ) ?? false
    );
  }, [dropItems]);

  // checkIsEverythingCorrect() mutates the dropItems and dragItems so we only want to call it when we need to
  const isEverythingCorrect = useMemo(() => {
    if (isCheckAnswerAtEnd) {
      return hasCheckedAnswer
        ? checkIsEverythingCorrect(dropItems, dragItems)
        : false;
    }

    return isEverythingAnswered
      ? checkIsEverythingCorrect(dropItems, dragItems)
      : false;
  }, [
    dropItems,
    dragItems,
    isEverythingAnswered,
    isCheckAnswerAtEnd,
    hasCheckedAnswer,
  ]);

  const showDraggableItems = useMemo(() => {
    if (isCheckAnswerAtEnd) {
      return !hasCheckedAnswer;
    }

    // checking answers as we go so hide when everything is correct
    return !isEverythingCorrect;
  }, [isCheckAnswerAtEnd, isEverythingCorrect, hasCheckedAnswer]);

  useSortableScrollManager(ref, [INTERACTIVE_DRAG_DROP]);

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: INTERACTIVE_DRAG_DROP,
      canDrop(item) {
        const dragItem = dragItems.find(dragItem => dragItem.id === item.id);
        if (!dragItem?.isDropped) {
          return false;
        }
        return true;
      },
      drop: (item: any) => {
        const { updatedDragItems, updatedDropItems } = handleItemReturn(
          item,
          dragItems,
          dropItems
        );
        setDragItems(updatedDragItems);
        setDropItems(updatedDropItems);
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        canDrop: monitor.canDrop(),
      }),
    }),
    [dragItems, dropItems]
  );

  useEffect(() => {
    setup();
    //eslint-disable-next-line
  }, [properties, attemptAnswers]);

  useEffect(() => {
    setAudioList(
      categoriseProperties.items.map(item => ({
        itemId: item.itemId,
        audioUrl: item.audio?.original,
      }))
    );
  }, [setAudioList, categoriseProperties.items]);

  return (
    <InteractiveResponseWrapper>
      <InteractiveResponsePlayContainer id={INTERACTIVE_CONTAINER_ID}>
        <InteractiveCanvas
          id={INTERACTIVE_CANVAS_ID}
          data-selector="interactive-categorise-window"
        >
          <InteractiveResponseTitle
            title={properties.interactiveResponseCategorise.title}
            subTitle={properties.interactiveResponseCategorise.subTitle}
          />

          <InteractiveResponseCheckAnswer
            isEverythingAnswered={isEverythingAnswered}
            isAnswerChecked={hasCheckedAnswer}
            isCheckAnswerAtEnd={isCheckAnswerAtEnd}
            dragItems={dragItems}
            dropItems={dropItems}
            onStartOver={() => setup(true)}
            onTryAgain={tryAgain}
            onCheckAnswer={handleCheckAnswers}
            moreInformation={
              properties.interactiveResponseCategorise.moreInformation
            }
          />

          {showDraggableItems && (
            <InteractiveCanvasDragZoneOuter>
              <InteractiveCanvasDragZone
                ref={node => {
                  drop(node);
                }}
                $isOver={isOver && canDrop}
                $isDropZoneClickable={!!dropZoneSelected}
                role="button"
                data-drop-zone-clickable="true"
                $isHighlighted={
                  !!dragItemSelected && dragItemSelected.isDropped
                }
                onClick={() => {
                  if (dragItemSelected?.isDropped) {
                    handleItemReturnToStack({
                      dragItems,
                      dropItems,
                      setDragItems,
                      setDropItems,
                    });
                  }
                }}
              >
                {dragItems.map((dragItem, index) => (
                  <InteractiveStackDragContainer
                    data-selector={`categorise-image-${index}`}
                    key={`${playAttempt}-${dragItem.id}-${index}-${playAudio}`}
                    $index={index}
                    id={dragItem.id}
                    $isTransitioning={isTransitioning}
                    $isDropped={dragItem.isDropped}
                  >
                    {!dragItem.isDropped && (
                      <InteractiveClickOrDrag
                        id={dragItem.id}
                        imageUrl={dragItem.question.image?.small}
                        isFit={dragItem.question.image?.isFit}
                        description={dragItem.question.image?.description || ''}
                        baseColour={dragItem.question.baseColour || null}
                        text={dragItem.question.text || null}
                        richtext={dragItem.question.richtext || null}
                        audioUrl={dragItem.question.audioUrl}
                        variant={dragItem.question.variant}
                        keyboard={keyboardProps}
                        onExpandImage={() =>
                          onExpandImage(
                            dragItem.question.image?.itemId || '',
                            dragItems
                          )
                        }
                        isDropZoneActive={!!dropZoneSelected}
                        onClick={() =>
                          handleDragItemClick({
                            dragItem,
                            handleOnDrop,
                            dropItems,
                            cancelKeyboardInteraction,
                          })
                        }
                      />
                    )}
                  </InteractiveStackDragContainer>
                ))}
              </InteractiveCanvasDragZone>
            </InteractiveCanvasDragZoneOuter>
          )}

          <Styled.InteractiveResponseCategories>
            {dropItems.map(dropItem => {
              return (
                <Styled.InteractiveResponseCategory
                  key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                >
                  <Styled.InteractiveResponseCategoryLabel>
                    {dropItem.label}
                  </Styled.InteractiveResponseCategoryLabel>
                  <InteractiveDrop
                    key={`${playAttempt}-${dropItem.id}-${playAudio}`}
                    id={dropItem.id}
                    item={dropItem}
                    dragItems={dragItems}
                    dropItems={dropItems}
                    onDrop={(destinationId, sourceId) => {
                      handleOnDrop(destinationId, sourceId);
                    }}
                    isDropZoneSelected={dropZoneSelected === dropItem.id}
                    isDragItemSelected={!!dragItemSelected}
                    onDropZoneClicked={id => {
                      // disable clicks if the answer has been submitted
                      // or the drop zone is full
                      if (
                        (isCheckAnswerAtEnd && hasCheckedAnswer) ||
                        (!isCheckAnswerAtEnd && isEverythingAnswered) ||
                        isDropZoneFull(dropItems, id)
                      ) {
                        return;
                      }

                      handleDropZoneClick({
                        handleOnDrop,
                        id,
                        cancelKeyboardInteraction,
                      });
                    }}
                  >
                    {dropItem.zones.map((zone, index) => {
                      const droppedDragItem = dragItems.find(
                        d =>
                          d.id === zone.dragItemId ||
                          d.id === zone.previewItemId
                      );
                      return (
                        <InteractiveDropDisplay
                          key={index}
                          isCorrect={zone.isCorrect}
                          hasDropped={!!zone.dragItemId}
                          isContinuousMarking={!isCheckAnswerAtEnd}
                          isVisible={isShowNumberOfItemsHint}
                          onIncorrectAnswer={element =>
                            returnItemToOriginalPosition({
                              element,
                              droppedDragItemId: droppedDragItem?.id || '',
                              scale: 1,
                              dropItem,
                              dropItems,
                              dragItems,
                              setDragItems,
                              setDropItems,
                            })
                          }
                        >
                          {droppedDragItem && (
                            <InteractiveClickOrDrag
                              key={droppedDragItem.id}
                              id={droppedDragItem.id}
                              imageUrl={droppedDragItem.question.image?.small}
                              isFit={droppedDragItem.question.image?.isFit}
                              description={
                                droppedDragItem.question.image?.description ||
                                ''
                              }
                              baseColour={
                                droppedDragItem.question.baseColour || null
                              }
                              text={droppedDragItem.question.text || null}
                              richtext={
                                droppedDragItem.question.richtext || null
                              }
                              audioUrl={droppedDragItem.question.audioUrl}
                              variant={droppedDragItem.question.variant}
                              keyboard={keyboardProps}
                              isDisabled={zone.isCorrect === true}
                              isCorrect={zone.isCorrect}
                              droppedAt={zone.dragItemId ? dropItem.label : ''}
                              overAt={zone.previewItemId ? dropItem.label : ''}
                              scaleTo={CATEGORISE_DROP_ITEM_SIZE}
                              isDropZoneActive={!!dropZoneSelected}
                              onClick={() => {
                                // disable clicks if the answer has been submitted
                                // or if checking answers immediately to match drag and drop behaviour
                                if (
                                  (isCheckAnswerAtEnd && hasCheckedAnswer) ||
                                  !isCheckAnswerAtEnd
                                ) {
                                  return;
                                }

                                handleDroppedDragItemClick({
                                  zone,
                                  droppedDragItem,
                                  dragItems,
                                  dropItems,
                                  setDragItems,
                                  setDropItems,
                                  handleOnDrop,
                                  cancelKeyboardInteraction,
                                });
                              }}
                            />
                          )}
                        </InteractiveDropDisplay>
                      );
                    })}
                  </InteractiveDrop>
                </Styled.InteractiveResponseCategory>
              );
            })}
          </Styled.InteractiveResponseCategories>

          <InteractiveResponseScreenReader keyboardState={keyboardState} />
        </InteractiveCanvas>
      </InteractiveResponsePlayContainer>
      <InteractiveResponseHelpControls
        howToPlay={categoriseProperties.howToPlay}
        toggleAudio={toggleAudio}
        playAudio={playAudio}
      />

      {isEverythingCorrect && (
        <InteractiveResponseControlsBottom>
          <InteractiveResponseStartAgainControls onClick={setup} />
        </InteractiveResponseControlsBottom>
      )}

      {dragItemSelected && <InteractiveResponseSelectAreaSnackbar />}
      {dropZoneSelected && <InteractiveResponseSelectItemSnackbar />}
    </InteractiveResponseWrapper>
  );
};

const InteractiveResponseCategorise = (props: Props) => {
  return (
    <InteractiveDragDrop>
      {props.properties.interactiveResponseCategorise.answerType ===
        RESPONSE_FREE || !props.showAnswer ? (
        <InteractiveCategoriseFree {...props} />
      ) : (
        <Interactive {...props} />
      )}
    </InteractiveDragDrop>
  );
};

export default InteractiveResponseCategorise;
